import { useMutation, useQuery } from '@tanstack/react-query'
import { Button, Modal, Select, SpinnerWithBackground, Text } from '@viapeople/components'
import type React from 'react'
import { useRef, useState } from 'react'
import { useToast } from '../../../utils/ToastContext'
import { queryClient, trpc, trpcClient } from '../../../utils/trpc'
import type { ColumnMapping, ManagerUpdate } from './csvManagerUtils'
import { extractCSVHeaders, parseCSVWithMapping } from './csvManagerUtils'

interface CycleManagerCorrectionModalProps {
	isOpen: boolean
	onClose: () => void
	cycleId: string
	organizationId: string
}

export const CycleManagerCorrectionModal: React.FC<CycleManagerCorrectionModalProps> = ({
	isOpen,
	onClose,
	cycleId,
	organizationId,
}) => {
	const toast = useToast()
	const fileInputRef = useRef<HTMLInputElement>(null)

	const [step, setStep] = useState<'upload' | 'mapping' | 'preview'>('upload')
	const [csvText, setCsvText] = useState<string>('')
	const [headers, setHeaders] = useState<string[]>([])
	const [csvDelimiter, setCsvDelimiter] = useState<string>(',')
	const [csvQuoteChar, setCsvQuoteChar] = useState<string>('"')
	const [columnMapping, setColumnMapping] = useState<ColumnMapping>({
		employeeId: '',
		primaryManagerId: '',
		secondaryManagerId: '',
		coManagerId: '',
	})
	const [updates, setUpdates] = useState<ManagerUpdate[]>([])
	const [_allParsedUpdates, setAllParsedUpdates] = useState<ManagerUpdate[]>([])
	const [unchangedCount, setUnchangedCount] = useState<number>(0)
	const [fileName, setFileName] = useState<string>('')
	const [isProcessing, setIsProcessing] = useState(false)

	// Get the cycle's assigned hierarchy file
	const { data: cycleHierarchyFile, isLoading: isLoadingHierarchy } = useQuery({
		...trpc.admin.cycles.hierarchies.show.queryOptions({
			cycleId,
			organizationId,
		}),
		enabled: !!cycleId && !!organizationId && isOpen,
	})

	const { mutate: bulkUpdateManagers, isPending: isUpdating } = useMutation({
		...trpc.admin.hierarchies.bulkUpdateManagers.mutationOptions(),
		onSuccess: (data) => {
			toast.showToast(data.message, 'info')
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.hierarchies.list.queryKey(),
				exact: false,
				refetchType: 'all',
			})
			queryClient.invalidateQueries({
				queryKey: trpc.admin.cycles.hierarchies.managerVisibility.list.queryKey(),
				exact: false,
				refetchType: 'all',
			})
			handleClose()
		},
		onError: (error) => {
			toast.showToast(`Error updating managers: ${error.message}`, 'error')
		},
	})

	const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (!file) return

		setIsProcessing(true)
		setFileName(file.name)

		try {
			const text = await file.text()
			const { headers: extractedHeaders, delimiter, quoteChar } = extractCSVHeaders(text)

			if (extractedHeaders.length === 0) {
				throw new Error('CSV file is empty or invalid')
			}

			setCsvText(text)
			setHeaders(extractedHeaders)
			setCsvDelimiter(delimiter)
			setCsvQuoteChar(quoteChar)
			setStep('mapping')
			toast.showToast('File uploaded successfully. Please map the columns.', 'info')
		} catch (error) {
			toast.showToast(`Error reading CSV: ${(error as Error).message}`, 'error')
			setCsvText('')
			setHeaders([])
		} finally {
			setIsProcessing(false)
		}
	}

	const handleColumnMappingComplete = async () => {
		if (!columnMapping.employeeId || !columnMapping.primaryManagerId) {
			toast.showToast('Please select at least Employee ID and Primary Manager columns', 'error')
			return
		}

		if (!cycleHierarchyFile?.id) {
			toast.showToast('No hierarchy file assigned to this cycle', 'error')
			return
		}

		setIsProcessing(true)
		try {
			const parsedUpdates = parseCSVWithMapping(csvText, columnMapping, csvDelimiter, csvQuoteChar)
			setAllParsedUpdates(parsedUpdates)

			// Fetch current hierarchy data for all employees in the CSV
			// We need to get the raw hierarchy data from the hierarchies table
			// to compare manager external IDs

			// Query hierarchies directly - we'll need to fetch all of them
			// since we need the manager external IDs which aren't in the cycle list endpoint
			const allHierarchies = await trpcClient.admin.cycles.hierarchies.list.query({
				organizationId: organizationId as string,
				cycleId: cycleId as string,
				page: 1,
				perPage: 10000,
			})

			// Create a map of current manager assignments by employee external ID
			const currentAssignments = new Map<
				string,
				{ primary?: string; secondary?: string; coManagerExternalIds: string[] }
			>()

			allHierarchies.data.forEach((row) => {
				currentAssignments.set(row.externalId, {
					primary: row.primaryManagerExternalId ?? undefined,
					secondary: row.secondaryManagerExternalId ?? undefined,
					coManagerExternalIds: (row.coManagers ?? []).map((cm: { externalId: string }) => cm.externalId).sort(),
				})
			})

			// Collect only the external IDs referenced in the CSV to avoid fetching all org users
			const allUserIds = new Set<string>()
			parsedUpdates.forEach((update) => {
				allUserIds.add(update.employeeExternalId)
				if (update.primaryManagerExternalId) allUserIds.add(update.primaryManagerExternalId)
				if (update.highLevelManagerExternalId) allUserIds.add(update.highLevelManagerExternalId)
				update.coManagerExternalIds?.forEach((id) => {
					allUserIds.add(id)
				})
			})

			if (allUserIds.size > 5000) {
				throw new Error(
					`CSV references ${allUserIds.size} unique user IDs. Maximum supported is 5,000. Please reduce the size of your CSV.`,
				)
			}

			// Query only the users referenced in the CSV (not all org users) to stay within payload limits
			const usersData = await trpcClient.admin.users.getNamesByExternalIds.query({
				organizationId: organizationId as string,
				externalIds: Array.from(allUserIds),
			})

			// Create a map of external ID to name
			const userNames = new Map<string, string>()
			usersData.forEach((user) => {
				if (user.externalId) {
					userNames.set(user.externalId, `${user.firstName} ${user.lastName}`.trim())
				}
			})

			// Filter out updates where nothing has changed and enrich with names
			const changedUpdates = parsedUpdates
				.filter((update) => {
					const current = currentAssignments.get(update.employeeExternalId)

					// If employee not found in current hierarchy, include it (new assignment)
					if (!current) return true

					// Check if primary manager changed
					const primaryChanged = update.primaryManagerExternalId !== current.primary

					// Check if secondary manager changed
					const secondaryChanged = update.highLevelManagerExternalId !== current.secondary

					// Check if co-managers changed (order-independent comparison)
					const uploadedCoManagers = [...(update.coManagerExternalIds ?? [])].sort()
					const currentCoManagers = current.coManagerExternalIds
					const coManagersChanged =
						uploadedCoManagers.length !== currentCoManagers.length ||
						uploadedCoManagers.some((id, i) => id !== currentCoManagers[i])

					return primaryChanged || secondaryChanged || coManagersChanged
				})
				.map((update) => ({
					...update,
					employeeName: userNames.get(update.employeeExternalId) || update.employeeExternalId,
					primaryManagerName: update.primaryManagerExternalId
						? userNames.get(update.primaryManagerExternalId) || update.primaryManagerExternalId
						: undefined,
					secondaryManagerName: update.highLevelManagerExternalId
						? userNames.get(update.highLevelManagerExternalId) || update.highLevelManagerExternalId
						: undefined,
					coManagerNames: update.coManagerExternalIds?.map((id) => userNames.get(id) || `Unknown (${id})`),
				}))

			const unchangedCountValue = parsedUpdates.length - changedUpdates.length
			setUnchangedCount(unchangedCountValue)

			// Check which employees have completed manager evaluations
			let employeesWithCompletedEvals: Set<string> = new Set()
			if (changedUpdates.length > 0) {
				try {
					const statusResult = await trpcClient.admin.hierarchies.checkManagerEvaluationStatus.query({
						organizationId: organizationId as string,
						cycleId: cycleId as string,
						employeeExternalIds: changedUpdates.map((u) => u.employeeExternalId),
					})
					employeesWithCompletedEvals = new Set(statusResult.employeesWithCompletedEvaluations)
				} catch (error) {
					console.error('Error checking manager evaluation status:', error)
					toast.showToast(
						'Could not check evaluation completion status. Completed evaluations may not be highlighted.',
						'error',
					)
				}
			}

			// Add completion status to updates
			const updatesWithStatus = changedUpdates.map((update) => ({
				...update,
				hasCompletedManagerEvaluation: employeesWithCompletedEvals.has(update.employeeExternalId),
			}))

			setUpdates(updatesWithStatus)

			setStep('preview')

			if (changedUpdates.length === 0) {
				toast.showToast('No changes detected. All manager assignments are already correct.', 'info')
			} else if (unchangedCountValue > 0) {
				toast.showToast(`Found ${changedUpdates.length} changes (${unchangedCountValue} already correct)`, 'info')
			} else {
				toast.showToast(`Successfully parsed ${changedUpdates.length} manager updates`, 'info')
			}
		} catch (error) {
			toast.showToast(`Error processing CSV: ${(error as Error).message}`, 'error')
			setUpdates([])
			setAllParsedUpdates([])
		} finally {
			setIsProcessing(false)
		}
	}

	const handleSubmit = () => {
		if (!cycleHierarchyFile?.id) {
			toast.showToast('This cycle does not have a hierarchy file assigned', 'error')
			return
		}

		if (updates.length === 0) {
			toast.showToast('Please upload a CSV file with manager updates', 'error')
			return
		}

		bulkUpdateManagers({
			organizationId,
			fileId: cycleHierarchyFile.id,
			updates,
		})
	}

	const handleClose = () => {
		setStep('upload')
		setCsvText('')
		setHeaders([])
		setCsvDelimiter(',')
		setCsvQuoteChar('"')
		setColumnMapping({
			employeeId: '',
			primaryManagerId: '',
			secondaryManagerId: '',
			coManagerId: '',
		})
		setUpdates([])
		setAllParsedUpdates([])
		setUnchangedCount(0)
		setFileName('')
		if (fileInputRef.current) {
			fileInputRef.current.value = ''
		}
		onClose()
	}

	const handleBack = () => {
		if (step === 'mapping') {
			setStep('upload')
			setCsvText('')
			setHeaders([])
		} else if (step === 'preview') {
			setStep('mapping')
			setUpdates([])
			setAllParsedUpdates([])
			setUnchangedCount(0)
		}
	}

	const headerOptions = headers.map((h) => ({ value: h, label: h }))

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			title='Correct Manager Assignments'
			className={step === 'preview' ? 'w-[98vw] max-w-[1800px]' : 'w-85 md:w-135'}>
			<div className='flex flex-col gap-6'>
				{/* Show assigned hierarchy */}
				<div>
					<Text variant='small' weight='semibold' className='mb-2'>
						Cycle Hierarchy
					</Text>
					{isLoadingHierarchy ? (
						<div className='h-10 flex items-center'>
							<SpinnerWithBackground backgroundClass='opacity-0' />
						</div>
					) : cycleHierarchyFile ? (
						<div className='p-3 bg-gray-50 rounded-lg'>
							<Text variant='small' className='text-gray-700'>
								{cycleHierarchyFile.name}
							</Text>
						</div>
					) : (
						<div className='p-3 bg-yellow-50 rounded-lg'>
							<Text variant='small' className='text-yellow-700'>
								No hierarchy file assigned to this cycle. Please assign one first.
							</Text>
						</div>
					)}
				</div>

				{cycleHierarchyFile && (
					<>
						{/* Step 1: Upload CSV */}
						{step === 'upload' && (
							<div>
								<Text variant='small' weight='semibold' className='mb-2'>
									Upload Manager Corrections CSV
								</Text>
								<Text variant='xsmall' className='mb-2 text-gray-600'>
									Upload a CSV file with employee IDs and their manager IDs. You'll map the columns in the next step.
								</Text>
								<input
									ref={fileInputRef}
									type='file'
									accept='.csv, text/csv'
									onChange={handleFileSelect}
									className='block w-full text-sm text-gray-500
                    file:mr-4 file:py-2 file:px-4
                    file:rounded-md file:border-0
                    file:text-sm file:font-semibold
                    file:bg-primary-50 file:text-primary-700
                    hover:file:bg-primary-100
                    cursor-pointer'
								/>
								{fileName && (
									<Text variant='xsmall' className='mt-2 text-green-600'>
										Loaded: {fileName}
									</Text>
								)}
							</div>
						)}

						{/* Step 2: Column Mapping */}
						{step === 'mapping' && (
							<div className='space-y-4'>
								<div>
									<Text variant='small' weight='semibold' className='mb-2'>
										Map CSV Columns
									</Text>
									<Text variant='xsmall' className='mb-4 text-gray-600'>
										Select which column in your CSV corresponds to each field.
									</Text>
								</div>

								<div>
									<Text variant='xsmall' weight='semibold' className='mb-1'>
										Employee ID Column <span className='text-red-500'>*</span>
									</Text>
									<Select
										options={[{ value: '', label: 'Select column...' }, ...headerOptions]}
										value={columnMapping.employeeId}
										onChange={(value: string) => setColumnMapping({ ...columnMapping, employeeId: value })}
									/>
								</div>

								<div>
									<Text variant='xsmall' weight='semibold' className='mb-1'>
										Primary Manager ID Column <span className='text-red-500'>*</span>
									</Text>
									<Select
										options={[{ value: '', label: 'Select column...' }, ...headerOptions]}
										value={columnMapping.primaryManagerId}
										onChange={(value: string) => setColumnMapping({ ...columnMapping, primaryManagerId: value })}
									/>
								</div>

								<div>
									<Text variant='xsmall' weight='semibold' className='mb-1'>
										Secondary Manager ID Column (Optional)
									</Text>
									<Select
										options={[{ value: '', label: 'None' }, ...headerOptions]}
										value={columnMapping.secondaryManagerId}
										onChange={(value: string) => setColumnMapping({ ...columnMapping, secondaryManagerId: value })}
									/>
								</div>

								<div>
									<Text variant='xsmall' weight='semibold' className='mb-1'>
										Co-Managers ID Column (Optional)
									</Text>
									<Select
										options={[{ value: '', label: 'None' }, ...headerOptions]}
										value={columnMapping.coManagerId}
										onChange={(value: string) => setColumnMapping({ ...columnMapping, coManagerId: value })}
									/>
								</div>
							</div>
						)}

						{/* Step 3: Preview */}
						{step === 'preview' && (
							<div>
								<div className='mb-4'>
									<Text variant='small' weight='semibold' className='mb-2'>
										Preview Changes
									</Text>
									{unchangedCount > 0 && (
										<div className='p-3 bg-blue-50 rounded-lg mb-3'>
											<Text variant='xsmall' className='text-blue-700'>
												{unchangedCount} employee{unchangedCount !== 1 ? 's' : ''} already have the correct manager
												assignments and will be skipped.
											</Text>
										</div>
									)}
									{updates.length === 0 ? (
										<div className='p-3 bg-green-50 rounded-lg'>
											<Text variant='small' className='text-green-700'>
												All manager assignments are already correct. No changes needed!
											</Text>
										</div>
									) : (
										<Text variant='xsmall' className='mb-2 text-gray-600'>
											{updates.length} employee{updates.length !== 1 ? 's' : ''} will be updated:
										</Text>
									)}
								</div>

								{/* Warning about manager evaluations */}
								{updates.length > 0 && (
									<div className='mb-3 p-3 bg-blue-50 border-l-4 border-blue-400 rounded-lg'>
										<div className='flex'>
											<div className='flex-shrink-0'>
												<svg
													className='h-5 w-5 text-blue-400'
													xmlns='http://www.w3.org/2000/svg'
													viewBox='0 0 20 20'
													fill='currentColor'
													aria-hidden='true'>
													<path
														fillRule='evenodd'
														d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z'
														clipRule='evenodd'
													/>
												</svg>
											</div>
											<div className='ml-3 flex-1'>
												<Text variant='small' weight='semibold' className='text-blue-800 mb-1'>
													Manager Task Reassignment
												</Text>
												{(() => {
													const completedCount = updates.filter((u) => u.hasCompletedManagerEvaluation).length
													return completedCount > 0 ? (
														<div className='mb-2 p-2 bg-amber-50 border border-amber-200 rounded'>
															<Text variant='xsmall' className='text-amber-800 font-medium'>
																⚠️ {completedCount} of {updates.length} employee
																{completedCount !== 1 ? 's have' : ' has'} already completed manager evaluation
																{completedCount !== 1 ? 's' : ''} (marked with "Completed" badge below)
															</Text>
														</div>
													) : null
												})()}
												<Text variant='xsmall' className='text-blue-700 mb-1'>
													<strong>What will happen:</strong>
												</Text>
												<ul className='text-blue-700 text-xs ml-4 mb-2 list-disc space-y-1'>
													<li>✅ Manager assignments in hierarchy will be updated</li>
													<li>
														✅ <strong>Only "Not-Started" manager evaluation tasks</strong> will be reassigned to new
														managers
													</li>
													<li>ℹ️ "In-Progress" tasks remain with the current manager (not reassigned)</li>
													<li>ℹ️ "Completed" tasks remain with the original manager (historical record)</li>
												</ul>
												<Text variant='xsmall' className='text-blue-700'>
													<strong>Important:</strong> If an employee has already started or completed their manager
													evaluation, those tasks will NOT be reassigned. Only tasks that haven't been started yet will
													move to the new manager.
												</Text>
											</div>
										</div>
									</div>
								)}

								{updates.length > 0 && (
									<div className='max-h-[400px] overflow-y-auto border border-gray-200 rounded-lg'>
										<table className='min-w-full divide-y divide-gray-200'>
											<thead className='bg-gray-50 sticky top-0'>
												<tr>
													<th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
														Employee ID
													</th>
													<th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
														Employee Name
													</th>
													<th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
														Primary Manager ID
													</th>
													<th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
														Primary Manager Name
													</th>
													<th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
														Secondary Manager ID
													</th>
													<th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
														Secondary Manager Name
													</th>
													<th className='px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase'>
														Co-Managers
													</th>
												</tr>
											</thead>
											<tbody className='bg-white divide-y divide-gray-200'>
												{updates.slice(0, 100).map((update) => (
													<tr key={update.employeeExternalId} className='hover:bg-gray-50'>
														<td className='px-3 py-2 text-xs text-gray-900 font-mono'>{update.employeeExternalId}</td>
														<td className='px-3 py-2 text-xs text-gray-900'>
															<div className='flex items-center gap-2'>
																<span>{update.employeeName || '-'}</span>
																{update.hasCompletedManagerEvaluation && (
																	<span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200'>
																		<svg
																			className='w-3 h-3 mr-1'
																			xmlns='http://www.w3.org/2000/svg'
																			viewBox='0 0 20 20'
																			fill='currentColor'
																			aria-hidden='true'>
																			<path
																				fillRule='evenodd'
																				d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
																				clipRule='evenodd'
																			/>
																		</svg>
																		Completed
																	</span>
																)}
															</div>
														</td>
														<td className='px-3 py-2 text-xs text-gray-900 font-mono'>
															{update.primaryManagerExternalId || '-'}
														</td>
														<td className='px-3 py-2 text-xs text-gray-900'>{update.primaryManagerName || '-'}</td>
														<td className='px-3 py-2 text-xs text-gray-900 font-mono'>
															{update.highLevelManagerExternalId || '-'}
														</td>
														<td className='px-3 py-2 text-xs text-gray-900'>{update.secondaryManagerName || '-'}</td>
														<td className='px-3 py-2 text-xs text-gray-900'>
															{update.coManagerNames && update.coManagerNames.length > 0 ? (
																<div className='flex flex-wrap gap-1'>
																	{update.coManagerNames.map((name) => (
																		<span
																			key={name}
																			className='inline-flex items-center px-2 py-0.5 rounded text-[10px] bg-blue-50 text-blue-700 border border-blue-100'>
																			{name}
																		</span>
																	))}
																</div>
															) : (
																'-'
															)}
														</td>
													</tr>
												))}
												{updates.length > 100 && (
													<tr>
														<td colSpan={7} className='px-3 py-2 text-xs text-gray-500 text-center'>
															... and {updates.length - 100} more
														</td>
													</tr>
												)}
											</tbody>
										</table>
									</div>
								)}
							</div>
						)}
					</>
				)}

				{/* Actions */}
				<div className='flex justify-between pt-4 border-t'>
					<div>
						{step !== 'upload' && (
							<Button variant='secondary' onClick={handleBack} disabled={isUpdating || isProcessing}>
								Back
							</Button>
						)}
					</div>
					<div className='flex gap-3'>
						<Button variant='secondary' onClick={handleClose} disabled={isUpdating || isProcessing}>
							Cancel
						</Button>
						{step === 'mapping' && (
							<Button
								variant='primary'
								onClick={handleColumnMappingComplete}
								disabled={!columnMapping.employeeId || !columnMapping.primaryManagerId || isProcessing}>
								Continue to Preview
							</Button>
						)}
						{step === 'preview' && (
							<Button
								variant='primary'
								onClick={handleSubmit}
								disabled={!cycleHierarchyFile || updates.length === 0 || isUpdating || isProcessing}>
								{isUpdating ? 'Updating...' : `Update ${updates.length} Managers`}
							</Button>
						)}
					</div>
				</div>
			</div>
		</Modal>
	)
}
